import { useRef, useState, useEffect } from "react";
import { get, set } from "idb-keyval";
import { 
  getAutographImagesByUserId, 
  uploadAutographImageSas, 
  getKiinteistoSasToken 
} from '../api/api';

export default function Signature({ userId, onAutographSaved }) {
  const [autographImage, setAutographImage] = useState(null);
  const [autographUrl, setAutographUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);

  useEffect(() => {
    if (!userId) return;   // <-- SAFE: hook still runs
    fetchAutograph();
  }, [userId]);
  
  const fetchAutograph = async () => {
    try {
      const res = await getAutographImagesByUserId(userId);
      const img = res?.data?.[0];
      if (!img) return;

      setAutographImage(img);

      const cached = await get(`autograph-${img.id}`);
      if (cached) {
        setAutographUrl(URL.createObjectURL(cached));
        return;
      }

      const sasRes = await getKiinteistoSasToken(img.azure_blob_name);
      const response = await fetch(sasRes.data.sasUrl);
      const blob = await response.blob();
      await set(`autograph-${img.id}`, blob);
      setAutographUrl(URL.createObjectURL(blob));

    } catch (err) {
      console.error("Failed to load autograph:", err);
    }
  };

  useEffect(() => {
    fetchAutograph();
  }, [userId]);


  const handleUploadAutograph = async (file) => {
    if (!file || !userId) return;

    setUploading(true);

    try {
      let resp = await uploadAutographImageSas(file, userId);
      const record = resp?.data ?? resp;    // ← ensure correct shape

      setAutographImage(record);
      setAutographUrl(URL.createObjectURL(file));
      await set(`autograph-${record.id}`, file);

      if (onAutographSaved) onAutographSaved(file);

      await fetchAutograph();

    } catch (err) {
      console.error("Autograph upload failed:", err);
    } finally {
      setUploading(false);
    }
  };


  // ---------------- CANVAS ----------------
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    lastXRef.current = e.clientX - rect.left;
    lastYRef.current = e.clientY - rect.top;
    isDrawingRef.current = true;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastXRef.current, lastYRef.current);
  };

  const draw = (e) => {
    if (!isDrawingRef.current || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.strokeStyle = "black";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    ctx.lineTo(x, y);
    ctx.stroke();

    lastXRef.current = x;
    lastYRef.current = y;
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveCanvasAutograph = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.warn("Canvas returned null blob — retrying…");
        setTimeout(saveCanvasAutograph, 100);
        return;
      }

      const file = new File([blob], "autograph.png", { type: "image/png" });
      await handleUploadAutograph(file);
    });
  };

  return (
    <div>
      <label className="form-label">Allekirjoituskuva</label>

      <div className="d-flex align-items-center gap-2 mb-2">
        {autographUrl ? (
          <img
            src={autographUrl}
            alt="Autograph"
            style={{ height: 80, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          <span>Ei ladattua allekirjoitusta</span>
        )}

        <label className="btn btn-outline-primary mb-0">
          {uploading ? "Tallennetaan..." : "Lataa kuva"}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => handleUploadAutograph(e.target.files[0])}
            disabled={uploading}
          />
        </label>
      </div>

      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        style={{ border: "1px solid #ccc", borderRadius: 4 }}
        onPointerDown={startDrawing}
        onPointerMove={draw}
        onPointerUp={stopDrawing}
        onPointerLeave={stopDrawing}
      />

      <div className="mt-2 d-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={clearCanvas}
        >
          Tyhjennä
        </button>

        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={saveCanvasAutograph}
          disabled={uploading}
        >
          {uploading ? "Tallennetaan..." : "Tallenna allekirjoitus"}
        </button>
      </div>
    </div>
  );
}
